import { envConfig } from '@src/utils/envConfig';
import {
  Button,
  ButtonVariants,
  FetchError,
  FormControlSizes,
  Get,
  HStack,
  IconName,
  Input,
  SearchSelect,
  Text,
  VStack,
} from '@talos/kyoko';
import { useEffect, useRef, useState } from 'react';

type TokenMethod = 'endpoint' | 'random' | 'none';

const TOKEN_METHOD_OPTIONS: { value: TokenMethod; label: string }[] = [
  { value: 'endpoint', label: 'Endpoint' },
  { value: 'random', label: 'Random' },
  { value: 'none', label: 'None' },
];

export function IFrameTestLayout() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [accessToken, setAccessToken] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [userUrl, setUserUrl] = useState<string>('');
  const [urlError, setUrlError] = useState<string>();
  const [isIFrameLoaded, setIsIFrameLoaded] = useState(false);
  const [tokenMethod, setTokenMethod] = useState<TokenMethod>('endpoint');

  // Get access token when user loads IFrame
  const getAccessToken = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(undefined);

      const accessTokenUrl = envConfig.VITE_AUTH_ACCESS_TOKEN_URL;
      if (!accessTokenUrl) {
        throw new Error('VITE_AUTH_ACCESS_TOKEN_URL environment variable is not configured');
      }

      interface AccessTokenResponse {
        access_token: string;
      }

      const data: AccessTokenResponse = await Get(accessTokenUrl, '');
      setAccessToken(data.access_token);

      // Access token retrieved for IFrame
      return true;
    } catch (err: unknown) {
      let errorMessage = 'Failed to get access token';
      if (err instanceof FetchError) {
        if (err.response.status === 401) {
          errorMessage = 'User not authenticated. Please log in first.';
        } else if (err.response.status === 404) {
          errorMessage = 'No access token available in session.';
        } else {
          errorMessage = `Failed to get access token: ${err.message}`;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      console.error('Error getting access token for IFrame:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Validate URL
  const validateUrl = (url: string): boolean => {
    try {
      const parsedUrl = new URL(url);
      // Only allow http: or https: protocols for security
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Handle URL input change
  const handleUrlChange = (value: string) => {
    setUserUrl(value);
    setUrlError(undefined);
  };

  // Handle load IFrame button click
  const handleLoadIFrame = async () => {
    if (!userUrl.trim()) {
      setUrlError('Please enter a URL');
      return;
    }

    if (!validateUrl(userUrl)) {
      setUrlError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setUrlError(undefined);
    setIsIFrameLoaded(false);
    setError(undefined);

    if (tokenMethod === 'endpoint') {
      const tokenRetrieved = await getAccessToken();
      // If there was an error getting the access token, stop here and show the error
      // The error state will be displayed by the error handling UI below
      if (!tokenRetrieved) {
        return;
      }
    } else if (tokenMethod === 'random') {
      setAccessToken(Math.random().toString(36).substring(2, 15));
    } else {
      setAccessToken(undefined);
    }

    setIsIFrameLoaded(true);
  };

  // Respond to REQUEST_ACCESS_TOKEN messages from the IFrame
  useEffect(() => {
    if (iframeRef.current && accessToken && isIFrameLoaded) {
      const IFrame = iframeRef.current;

      const sendToken = () => {
        try {
          console.info('Sending access token to IFrame via PostMessage', { accessToken });
          // Send the access token to the IFrame via PostMessage
          IFrame.contentWindow?.postMessage({ type: 'ACCESS_TOKEN', token: accessToken }, '*');
        } catch (err) {
          console.warn('Could not send access token to IFrame (likely cross-origin):', err);
        }
      };

      // Listen for REQUEST_ACCESS_TOKEN messages from the IFrame
      const handleMessage = (event: MessageEvent) => {
        // Verify the message is from our IFrame
        if (event.source === IFrame.contentWindow && event.data?.type === 'REQUEST_ACCESS_TOKEN') {
          sendToken();
        }
      };

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [accessToken, isIFrameLoaded]);

  // Show loading state
  if (isLoading) {
    return (
      <VStack h="100%" w="100%" alignItems="center" justifyContent="center" gap="spacing16">
        <Text size="fontSizeMd" fontWeight="medium">
          Loading Access Token...
        </Text>
      </VStack>
    );
  }

  // Show error state
  if (error && !isIFrameLoaded) {
    return (
      <VStack h="100%" w="100%" alignItems="center" justifyContent="center" gap="spacing16">
        <Text size="fontSizeMd" fontWeight="medium" color="colorTextAttention">
          Error Loading Access Token
        </Text>
        <Text size="fontSizeSm" color="colorTextSubtle" textAlign="center" maxWidth="400px">
          {error}
        </Text>
        <HStack gap="spacing12">
          <Button
            onClick={() => {
              setError(undefined);
              setAccessToken(undefined);
            }}
            startIcon={IconName.Refresh}
          >
            Retry
          </Button>
          <Button
            variant={ButtonVariants.Muted}
            onClick={() => {
              setError(undefined);
              setAccessToken(undefined);
              setIsIFrameLoaded(true);
            }}
          >
            Continue Without Token
          </Button>
        </HStack>
      </VStack>
    );
  }

  // Show URL input interface if IFrame not loaded yet
  if (!isIFrameLoaded) {
    return (
      <VStack h="100%" w="100%" alignItems="center" justifyContent="center" gap="spacing24" p="spacing24">
        <VStack alignItems="center" gap="spacing16" maxWidth="600px">
          <Text size="fontSizeLg" fontWeight="semibold" textAlign="center">
            IFrame Test Layout
          </Text>
          <Text size="fontSizeSm" color="colorTextSubtle" textAlign="center">
            Enter the URL of the application you want to embed. An access token will be automatically retrieved and
            passed to the IFrame via PostMessage.
          </Text>

          <VStack w="100%" gap="spacing8">
            <Text size="fontSizeSm" fontWeight="medium">
              IFrame URL
            </Text>
            <Input
              value={userUrl}
              onChange={e => handleUrlChange(e.target.value)}
              placeholder="https://example.com"
              invalid={!!urlError}
              width="100%"
            />
            {urlError && (
              <Text size="fontSizeSm" color="colorTextAttention">
                {urlError}
              </Text>
            )}
          </VStack>

          <VStack w="100%" gap="spacing12">
            <HStack w="100%" gap="spacing8">
              <Text size="fontSizeSm" fontWeight="medium">
                Token
              </Text>
              <SearchSelect<{ value: TokenMethod; label: string }>
                options={TOKEN_METHOD_OPTIONS}
                selection={TOKEN_METHOD_OPTIONS.find(o => o.value === tokenMethod)}
                onChange={item => item && setTokenMethod(item.value)}
                getLabel={item => item.label}
                w="100%"
              />
              <Button
                onClick={handleLoadIFrame}
                startIcon={IconName.ExternalLink}
                disabled={!userUrl.trim() || !!urlError}
                width="100%"
              >
                Load IFrame
              </Button>
            </HStack>
          </VStack>
        </VStack>
      </VStack>
    );
  }

  // Show IFrame when loaded
  return (
    <VStack h="100%" w="100%" gap="spacing8">
      {/* Header with URL info and reload button */}
      <HStack w="100%" justifyContent="space-between" alignItems="center" p="spacing12" borderRadius="borderRadius8">
        <VStack gap="spacing4" alignItems="flex-start">
          <Text size="fontSizeSm" fontWeight="medium">
            Embedded: {userUrl}
          </Text>
          {accessToken && (
            <Text size="fontSizeSm" color="colorTextSubtle">
              Access token: {accessToken.substring(0, 20)}
            </Text>
          )}
        </VStack>
        <Button
          variant={ButtonVariants.Muted}
          size={FormControlSizes.Small}
          onClick={() => {
            setIsIFrameLoaded(false);
            setAccessToken(undefined);
          }}
          startIcon={IconName.Refresh}
        >
          Change URL
        </Button>
      </HStack>

      {/* IFrame */}
      <iframe
        ref={iframeRef}
        src={userUrl}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block',
          flex: 1,
        }}
        title="Embedded Content"
        allow="fullscreen"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
      />
    </VStack>
  );
}

export default IFrameTestLayout;
